import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { API } from '@/App';

// Entitlement context
const EntitlementContext = createContext(null);

// Entitlement states
export const EntitlementState = {
  FREE: 'free',
  EXPLORER_PRO: 'explorer_pro',
  DUO_OWNER: 'duo_owner',
  DUO_MEMBER: 'duo_member',
  FOUNDING_MEMBER: 'founding_member',
};

// Feature constants for checking access
export const Features = {
  // AI & Planning
  AI_SUGGESTIONS: 'ai_suggestions',
  AI_TRIP_PLANNER: 'ai_trip_planner',
  AI_HIDDEN_GEMS: 'ai_hidden_gems',
  
  // Memories & Media
  UNLIMITED_MEMORIES: 'unlimited_memories',
  UNLIMITED_PHOTOS: 'unlimited_photos',
  TIMELINE_REPLAY: 'timeline_replay',
  SCRATCH_OFF_MAP: 'scratch_off_map',
  
  // Stats & Insights
  ADVANCED_WORLD_STATS: 'advanced_world_stats',
  RANK_HISTORY: 'rank_history',
  PERCENTILE_INSIGHTS: 'percentile_insights',
  CITY_HEATMAPS: 'city_heatmaps',
  
  // Themes & Customization
  EXCLUSIVE_MAP_THEMES: 'exclusive_map_themes',
  PROFILE_THEMES: 'profile_themes',
  CUSTOM_CATEGORIES: 'custom_categories',
  
  // Budget & Export
  BUDGET_TRACKING: 'budget_tracking',
  BUDGET_EXPORTS: 'budget_exports',
  PDF_EXPORT: 'pdf_export',
  PUBLIC_LINKS: 'public_links',
  
  // Premium Features
  ANNIVERSARY_VAULT: 'anniversary_vault',
  STREAK_REWARDS: 'streak_rewards',
  OFFLINE_MODE: 'offline_mode',
  
  // Duo-exclusive
  COUPLE_DASHBOARD: 'couple_dashboard',
  SHARED_ANNIVERSARY_VAULT: 'shared_anniversary_vault',
  JOINT_WORLD_HEATMAP: 'joint_world_heatmap',
  SHARED_YEARLY_REPLAY: 'shared_yearly_replay',
  DUO_BADGES: 'duo_badges',
  COUPLE_MILESTONE_INSIGHTS: 'couple_milestone_insights',
  COMPATIBILITY_ARCHETYPES: 'compatibility_travel_archetypes',
  JOINT_STREAK_PROTECTION: 'joint_streak_protection',
};

// Provider component
export const EntitlementProvider = ({ children }) => {
  const [entitlements, setEntitlements] = useState(null);
  const [upgradeRecommendation, setUpgradeRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntitlements = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/subscription/entitlements`);
      setEntitlements(res.data.entitlements);
      setUpgradeRecommendation(res.data.upgrade_recommendation);
      setError(null);
    } catch (err) {
      // If not authenticated, default to free tier
      if (err.response?.status === 401) {
        setEntitlements({
          entitlement_state: EntitlementState.FREE,
          plan_name: 'Free',
          has_paid_access: false,
          has_duo_access: false,
          features: [],
          limits: { max_items: -1, max_photos_per_item: 5 },
        });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  // Phase 3.4: Re-fetch entitlements on app foreground (visibility change)
  // and deep-link return — unlocks premium immediately after payment
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchEntitlements();
      }
    };
    // Native app bridge: refresh on foreground / checkout return / premium activation
    const handleNativeRefresh = () => {
      fetchEntitlements();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('duolyst:refresh-entitlements', handleNativeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('duolyst:refresh-entitlements', handleNativeRefresh);
    };
  }, [fetchEntitlements]);

  const value = {
    entitlements,
    upgradeRecommendation,
    loading,
    error,
    refetch: fetchEntitlements,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

// Main hook
export const useEntitlements = () => {
  const context = useContext(EntitlementContext);
  
  // Standalone fetch when no provider context exists
  const [standaloneData, setStandaloneData] = useState(null);
  const [standaloneLoading, setStandaloneLoading] = useState(true);
  const [standaloneError, setStandaloneError] = useState(null);
  
  const standaloneRefetch = useCallback(async () => {
    try {
      setStandaloneLoading(true);
      const res = await axios.get(`${API}/subscription/entitlements`);
      setStandaloneData(res.data.entitlements);
      setStandaloneError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        setStandaloneData({
          entitlement_state: EntitlementState.FREE,
          plan_name: 'Free',
          has_paid_access: false,
          has_duo_access: false,
          is_founding_member: false,
          features: [],
          limits: { max_items: -1, max_photos_per_item: 5 },
        });
      } else {
        setStandaloneError(err.message);
      }
    } finally {
      setStandaloneLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!context) {
      standaloneRefetch();
    }
  }, [context, standaloneRefetch]);
  
  // Use context if available, otherwise use standalone data
  const entitlements = context ? (context.entitlements ?? null) : standaloneData;
  const upgradeRecommendation = context?.upgradeRecommendation ?? null;
  const loading = context ? (context.loading ?? false) : standaloneLoading;
  const error = context ? (context.error ?? null) : standaloneError;
  const refetch = context ? (context.refetch ?? (() => {})) : standaloneRefetch;

  // Derive state from entitlements
  const isFoundingMember = entitlements?.is_founding_member || false;
  
  // Universal access: founders, admins get EVERYTHING unlocked
  const hasUniversalAccess = isFoundingMember || entitlements?.entitlement_state === 'founding_member';
  
  const hasPaidAccess = hasUniversalAccess || entitlements?.has_paid_access || false;
  const hasDuoAccess = hasUniversalAccess || entitlements?.has_duo_access || false;
  const isDuoOwner = entitlements?.is_duo_owner || false;
  const isDuoMember = entitlements?.is_duo_member || false;
  const isGracePeriod = entitlements?.is_grace_period || false;

  // ALL useCallback hooks MUST be defined unconditionally at top level
  const canAccessFeature = useCallback((feature) => {
    if (hasUniversalAccess) return true;
    if (!entitlements) return false;
    return entitlements.features?.includes(feature) || false;
  }, [entitlements, hasUniversalAccess]);

  const canAccessExplorerPro = useCallback((feature = null) => {
    if (hasUniversalAccess) return true;
    if (!hasPaidAccess) return false;
    if (feature) return entitlements?.features?.includes(feature) || false;
    return true;
  }, [hasPaidAccess, entitlements, hasUniversalAccess]);

  const canAccessDuo = useCallback((feature = null) => {
    if (hasUniversalAccess) return true;
    if (!hasDuoAccess) return false;
    if (feature) return entitlements?.features?.includes(feature) || false;
    return true;
  }, [hasDuoAccess, entitlements, hasUniversalAccess]);

  const getLimits = useCallback(() => {
    return entitlements?.limits || {
      max_items: -1,
      max_partners: 1,
      max_groups: 1,
      max_photos_per_item: 5,
      max_memories: 20,
    };
  }, [entitlements]);

  const isWithinLimit = useCallback((limitType, currentCount) => {
    const limits = entitlements?.limits || {
      max_items: -1,
      max_partners: 1,
      max_groups: 1,
      max_photos_per_item: 5,
      max_memories: 20,
    };
    const limit = limits[limitType];
    if (limit === -1) return true; // Unlimited
    return currentCount < limit;
  }, [entitlements]);

  const getEntitlementState = useCallback(() => {
    return entitlements?.entitlement_state || EntitlementState.FREE;
  }, [entitlements]);

  const getPlanName = useCallback(() => {
    return entitlements?.plan_name || 'Free';
  }, [entitlements]);

  return {
    // Raw data
    entitlements,
    upgradeRecommendation,
    loading,
    error,
    refetch,
    
    // Quick checks
    hasPaidAccess,
    hasDuoAccess,
    hasUniversalAccess,
    isFoundingMember,
    isDuoOwner,
    isDuoMember,
    isGracePeriod,
    
    // Feature access
    canAccessFeature,
    canAccessExplorerPro,
    canAccessDuo,
    
    // Limits
    getLimits,
    isWithinLimit,
    
    // State
    getEntitlementState,
    getPlanName,
    
    // Convenience flags
    canUseAI: hasUniversalAccess || entitlements?.can_use_ai || false,
    canExportPdf: hasUniversalAccess || entitlements?.can_export_pdf || false,
    canTrackBudget: hasUniversalAccess || entitlements?.can_track_budget || false,
    canUseOffline: hasUniversalAccess || entitlements?.can_use_offline || false,
    canUseCoupleDashboard: hasUniversalAccess || entitlements?.can_use_couple_dashboard || false,
    canUseSharedVault: hasUniversalAccess || entitlements?.can_use_shared_vault || false,
  };
};

// Standalone hook for when not using provider
const useStandaloneEntitlements = () => {
  const [entitlements, setEntitlements] = useState(null);
  const [upgradeRecommendation, setUpgradeRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEntitlements = async () => {
      try {
        const res = await axios.get(`${API}/subscription/entitlements`);
        setEntitlements(res.data.entitlements);
        setUpgradeRecommendation(res.data.upgrade_recommendation);
      } catch (err) {
        // Default to free tier
        setEntitlements({
          entitlement_state: EntitlementState.FREE,
          plan_name: 'Free',
          has_paid_access: false,
          has_duo_access: false,
          features: [],
          limits: { max_items: -1, max_photos_per_item: 5 },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEntitlements();
  }, []);

  const isFoundingMember = entitlements?.is_founding_member || false;
  const hasUniversalAccess = isFoundingMember || entitlements?.entitlement_state === 'founding_member';

  return {
    entitlements,
    upgradeRecommendation,
    loading,
    error: null,
    refetch: () => {},
    hasPaidAccess: hasUniversalAccess || entitlements?.has_paid_access || false,
    hasDuoAccess: hasUniversalAccess || entitlements?.has_duo_access || false,
    hasUniversalAccess,
    isFoundingMember,
    isDuoOwner: entitlements?.is_duo_owner || false,
    isDuoMember: entitlements?.is_duo_member || false,
    isGracePeriod: entitlements?.is_grace_period || false,
    canAccessFeature: (feature) => hasUniversalAccess || entitlements?.features?.includes(feature) || false,
    canAccessExplorerPro: () => hasUniversalAccess || entitlements?.has_paid_access || false,
    canAccessDuo: () => hasUniversalAccess || entitlements?.has_duo_access || false,
    getLimits: () => entitlements?.limits || { max_items: -1, max_photos_per_item: 5 },
    isWithinLimit: () => true,
    getEntitlementState: () => entitlements?.entitlement_state || EntitlementState.FREE,
    getPlanName: () => entitlements?.plan_name || 'Free',
    canUseAI: false,
    canExportPdf: false,
    canTrackBudget: false,
    canUseOffline: false,
    canUseCoupleDashboard: false,
    canUseSharedVault: false,
  };
};

// Feature gate component for declarative usage
export const FeatureGate = ({ 
  feature, 
  requiredTier = 'explorer_pro', // 'explorer_pro' or 'duo'
  children, 
  fallback = null,
  showUpgradePrompt = false,
  onUpgradeClick = null,
}) => {
  const { canAccessFeature, canAccessExplorerPro, canAccessDuo } = useEntitlements();

  const hasAccess = requiredTier === 'duo' 
    ? canAccessDuo(feature)
    : canAccessExplorerPro(feature);

  if (hasAccess) {
    return children;
  }

  if (showUpgradePrompt && onUpgradeClick) {
    return (
      <div onClick={onUpgradeClick} className="cursor-pointer">
        {fallback}
      </div>
    );
  }

  return fallback;
};

export default useEntitlements;
